import type { ReactNode } from 'react';
import { PageHeader } from '@/shared/ui';
import styles from './SettingsFrame.module.css';

interface Props {
  /** Shown as a sub-section of a Settings page instead of a page of its own. */
  embedded?: boolean;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  /** The page wrapper's class when standing alone. */
  pageClassName?: string;
  /** The heading's id, so the section can be labelled by it. */
  id?: string;
  /** 2: a sub-section of the Settings page; 3: one inside a sub-section (Online Visibility > Review Responder). */
  level?: 2 | 3;
  children: ReactNode;
}

/**
 * A page that can also be a sub-section of a Settings page: alone, its own page header; embedded, a section heading
 * on the Settings page that holds it (Settings > Team & Staff, Operations, Communication and the rest).
 */
export function SettingsFrame({
  embedded = false,
  title,
  subtitle,
  actions,
  pageClassName,
  id,
  level = 2,
  children,
}: Props) {
  if (!embedded)
    return (
      <div className={pageClassName}>
        <PageHeader title={title} subtitle={subtitle} actions={actions} />
        {children}
      </div>
    );
  const Heading = level === 3 ? 'h3' : 'h2';
  return (
    <section
      className={level === 3 ? styles.subsection : styles.section}
      aria-labelledby={id}
      id={id ? `${id}-section` : undefined}
    >
      <div className={level === 3 ? styles.subhead : styles.head}>
        <div>
          <Heading id={id} className={level === 3 ? styles.subtitleHeading : styles.title}>
            {title}
          </Heading>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** A sub-section with nothing in it yet, or switched off for this practice. */
export function SettingsNote({ children }: { children: ReactNode }) {
  return <p className={styles.note}>{children}</p>;
}

/** A sub-section of a Settings page that holds sub-sub-sections (Online Visibility: SEO / AEO, Review Responder). */
export function SettingsGroup({
  id,
  title,
  subtitle,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className={styles.section} aria-labelledby={id}>
      <div className={styles.head}>
        <div>
          <h2 id={id} className={styles.title}>
            {title}
          </h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}
